import React from 'react';
import { Modal, ModalActions, ModalContent } from '../../../Modal';
import { AsyncButton } from '../../../Button';
import { useAddPublicICSSubscription, useDeletePublicICSSubscription, usePublicICSSubscriptionQuery } from '@uplift/queries';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { useIsMobile } from '@uplift/hooks';
import { InstructionAppleCalendar, InstructionGoogleCalendar, InstructionNativeCalendarWithLinkButton } from './Instructions.component';
import { Alert } from '../../../Alert';
import { Loader } from '../../../Loader';


export type PublicICSSubscriptionModalProps = {
  open: boolean;
  onClose: () => void;
};


type ICSSubscriptionTab = 'native-calendar' | 'google-calendar' | 'apple-calendar';

const PublicICSSubscriptionModalContent: React.FC<{ open: boolean }> = (props) => {
  const { data: publicIcsSubscription } = usePublicICSSubscriptionQuery({ enabled: props.open, suspense: true });
  const deleteICSSubscriptionMutation = useDeletePublicICSSubscription();
  const addICSSubscriptionMutation = useAddPublicICSSubscription();
  const [ tab, setTab ] = React.useState<ICSSubscriptionTab>('native-calendar');
  const isMobile = useIsMobile();

  return (
    <>
      <ModalContent noPadding dividers>
        <Box display="flex" flexDirection="column">
          {publicIcsSubscription?.subscriptionUrl ? (
            <>
              <Tabs
                orientation={isMobile ? 'vertical' : 'horizontal'}
                indicatorColor="secondary"
                textColor="secondary"
                value={tab}
                onChange={(_, newValue: ICSSubscriptionTab) => setTab(newValue)} variant="fullWidth"
              >
                <Tab value="native-calendar" label="One-Click Subscribe" color="secondary" />
                <Tab value="google-calendar" label="Google Calendar" color="secondary" />
                <Tab value="apple-calendar" label="Apple Calendar" color="secondary" />
              </Tabs>
              {isMobile && <Divider />}
              <Box px={3} py={2}>
                {tab === 'native-calendar' && <InstructionNativeCalendarWithLinkButton icsLink={publicIcsSubscription.directSubscriptionUrl} />}
                {tab === 'google-calendar' && <InstructionGoogleCalendar icsLink={publicIcsSubscription.subscriptionUrl} />}
                {tab === 'apple-calendar' && <InstructionAppleCalendar icsLink={publicIcsSubscription.subscriptionUrl} />}
              </Box>
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography>
                Automatically add your BetterHelp sessions to your calendar so you never miss one.
              </Typography>
              <Typography>
                This is a <strong>one-time setup</strong>. Once enabled, your calendar will stay up to date automatically using a private link.
              </Typography>
            </Box>
          )}
        </Box>
      </ModalContent>
      <ModalActions>
        {publicIcsSubscription?.subscriptionUrl ? (
          <Box display="flex" flex={1} alignItems="center" flexDirection="column" gap={2} boxSizing="border-box">
            <Alert severity="info" sx={{ flex: 1, width: '100%' }}>
              <strong>Note:</strong> If you disable your subscription, this link will stop syncing. You can re-enable it anytime to generate a new one.
            </Alert>
            <AsyncButton fullWidth variant="contained" color="error" onClick={() => deleteICSSubscriptionMutation.mutateAsync()}>Disable sync</AsyncButton>
          </Box>
        ) : (
          <AsyncButton variant="contained" color="secondary" onClick={() => addICSSubscriptionMutation.mutateAsync()}>Enable sync</AsyncButton>
        )}
      </ModalActions>
    </>
  );
};


export const PublicICSSubscriptionModal: React.FC<PublicICSSubscriptionModalProps> = (props) => {
  const isMobile = useIsMobile();

  return (
    <Modal
      fullScreen={isMobile}
      open={props.open}
      onClose={props.onClose}
      title="Add sessions to your personal calendar"
      maxWidth="md"
      fullWidth
      maxWidthPx="608px"
    >
      <React.Suspense fallback={<Loader minHeight="300px" />}>
        <PublicICSSubscriptionModalContent open={props.open} />
      </React.Suspense>
    </Modal>
  );
};
